import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { getTeamResults } from "@/lib/api";
import { useViewerHostKey } from "@/lib/queryClient";
import { hasShootout, shootoutOutcome } from "@/features/matches/matchResult";
import type { Match, TeamPerformance, Tournament } from "@/lib/types";
import { sideForTeam } from "../teamPerformance";
import { TournamentEventContent, TournamentRow } from "./TournamentRow";

export function LatestTournamentFinish({
  tournament,
  legacy,
  teamSlug,
  seasonId,
  team,
}: {
  tournament?: Tournament;
  legacy?: TeamPerformance["legacy_tournaments"][number];
  teamSlug: string;
  seasonId: string;
  team: { id: string; name: string };
}) {
  // Read one bounded page for this event, independently of the match-list filters.
  // Only an explicitly labelled, scored final can supply the deciding score.
  const queryKey = useViewerHostKey("team-results", {
    slug: teamSlug,
    season: seasonId,
    tournament: tournament?.id,
    latestFinish: true,
  });
  const results = useQuery({
    queryKey,
    queryFn: () => getTeamResults(teamSlug, {
      season_id: seasonId,
      competition: "tournament",
      tournament_id: tournament?.id,
      limit: 100,
    }),
    enabled: !!tournament,
  });
  const matches = tournament ? results.data?.matches ?? [] : legacy?.matches ?? [];
  const final = [...matches]
    .sort((a, b) => b.date.localeCompare(a.date) || b.time.localeCompare(a.time))
    .find((match) =>
      (!tournament || match.tournament_id === tournament.id) &&
      match.stage?.trim().toLowerCase() === "final" &&
      match.match_status === "completed" &&
      match.score_home != null && match.score_away != null &&
      sideForTeam(match, team.id, team.name) !== "none",
    );
  const decider = final ? finalDetails(final, team) : undefined;
  if (tournament) {
    return (
      <TournamentRow
        tournament={tournament}
        teamSlug={teamSlug}
        seasonId={seasonId}
        latest
        decider={decider}
      />
    );
  }
  if (!legacy) return null;
  const dates = legacy.matches.map((match) => match.date).filter(Boolean).sort();
  return (
    <Link
      className="scorebook-event scorebook-directory-event scorebook-latest-event"
      to="/teams/$teamSlug"
      params={{ teamSlug }}
      search={{ season: seasonId, view: "tournaments" }}
    >
      <TournamentEventContent
        tournament={{
          name: legacy.name,
          status: "completed",
          start_date: dates[0] ?? "",
          end_date: dates.at(-1) ?? "",
          location: final?.location ?? "",
          division: "",
          outcome: legacy.outcome,
          summary: legacy.summary,
        }}
        decider={decider}
        latest
      />
    </Link>
  );
}

function finalDetails(match: Match, team: { id: string; name: string }) {
  const home = sideForTeam(match, team.id, team.name) === "home";
  return {
    score: `${home ? match.score_home : match.score_away}–${home ? match.score_away : match.score_home}`,
    penalties: hasShootout(match)
      ? `${shootoutOutcome(match, home) === "won" ? "Won" : "Lost"} on penalties · ${home ? match.pk_home : match.pk_away}–${home ? match.pk_away : match.pk_home}`
      : undefined,
  };
}
